import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel, FreeMode, Keyboard } from "swiper/modules";
import "swiper/css";
import Header from "./component/NavbarWithMegaMenu";
import Footer from "./components/footer/Footer";
import HeroSlide from "./pages/Home/HeroSlide";
import NewsSlide from "./pages/Home/NewsSlide";
import CalendarSlide from "./pages/Home/CalendarSlide";
import FooterSlide from "./pages/Home/FooterSlide";
import ScrollToTop from "./ScrollToTop";
import SliderImages from "./components/SliderImages";
import WeeklyPopup from "./components/WeeklyPopup/WeeklyPopup";
import { initializeAnalytics, trackPageView } from "./utils/analytics";
import "./App.css";
import "./index.css";

// Lazy Load Pages
const Haberler = lazy(() => import("./pages/Haberler/Haberler"));
const SerdivanTarihi = lazy(() => import("./pages/Serdivan/SerdivanTarihi"));
const SerdivanCografya = lazy(() => import("./pages/Serdivan/SerdivanCografya"));
const SerdivanGelisim = lazy(() => import("./pages/Serdivan/SerdivanGelisim"));
const Muhtarliklar = lazy(() => import("./pages/Serdivan/Muhtarliklar"));
const KardesSehirler = lazy(() => import("./pages/Serdivan/KardesSehirler"));
const BelediyeBaskani = lazy(() => import("./pages/Kurumsal/BelediyeBaskani"));
const Iletisim = lazy(() => import("./pages/Iletisim"));
const MeclisGorevleri = lazy(() => import("./pages/Kurumsal/BelediyeMeclisi/MeclisGorevleri"));
const Hizmetlerimiz = lazy(() => import("./pages/Hizmetlerimiz/Hizmetlerimiz"));
const Duyurular = lazy(() => import("./pages/Duyurular/Duyurular"));
const Etik = lazy(() => import("./pages/Kurumsal/Etik/Etik"));
const Vizyon = lazy(() => import("./pages/Kurumsal/Yonetim/Vizyon"));
const StratejikPlanlar = lazy(() => import("./pages/Kurumsal/Yonetim/StratejikPlanlar"));
const PerformansProgramlari = lazy(() => import("./pages/Kurumsal/Yonetim/PerformansProgramlari"));
const FaaliyetRaporlari = lazy(() => import("./pages/Kurumsal/Yonetim/FaaliyetRaporlari"));
const MaliBeklenti = lazy(() => import("./pages/Kurumsal/Yonetim/MaliBeklenti"));
const EncumenGorev = lazy(() => import("./pages/Kurumsal/BelediyeEncumeni/EncumenGorev"));
const HaberGoruntuleme = lazy(() => import("./pages/Haberler/HaberGoruntuleme/HaberGoruntuleme"));
const OrganizasyonSemasiPage = lazy(() => import("./pages/Kurumsal/Yonetim/OrganizasyonSemasi"));
const MeclisUyeleri = lazy(() => import("./pages/Kurumsal/BelediyeMeclisi/MeclisUyeleri"));
const MeclisGundemleri = lazy(() => import("./pages/Kurumsal/BelediyeMeclisi/MeclisGundemleri"));
const MeclisKararlari = lazy(() => import("./pages/Kurumsal/BelediyeMeclisi/MeclisKararlari"));
const KomisyonRaporlari = lazy(() => import("./pages/Kurumsal/BelediyeMeclisi/KomisyonRaporlari"));
const BaskanYardimcilari = lazy(() => import("./pages/Kurumsal/BaskanYardimcilari"));
const BelediyeEncumeni = lazy(() => import("./pages/Kurumsal/BelediyeEncumeni/BelediyeEncumeni"));
const OnlineIslemler = lazy(() => import("./pages/OnlineIslemler"));
const Mudurlukler = lazy(() => import("./pages/Kurumsal/Mudurlukler"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Kvkk = lazy(() => import("./pages/Kvkk"));
const Trafikpark = lazy(() => import("./pages/Trafikpark/Trafikpark"));
const Planetaryum = lazy(() => import("./pages/Planetaryum/Planetaryum"));
const PlanetaryumGaleri = lazy(() => import("./pages/Planetaryum/PlanetaryumGaleri"));
const TrafikparkGaleri = lazy(() => import("./pages/Trafikpark/TrafikparkGaleri"));
const EventDetail = lazy(() => import("./pages/Etkinlik/EventDetail"));
const EtkinlikTakvimi = lazy(() => import("./pages/Etkinlik/EtkinlikTakvimi"));
const Serbim = lazy(() => import("./pages/Serbim/Serbim"));
const NobetciEczaneler = lazy(() => import("./pages/NobetciEczaneler/NobetciEczaneler"));
const SporOkullariKayit = lazy(() => import("./pages/SporOkullari/SporOkullariKayit"));
const SporOkullariCekilis = lazy(() => import("./pages/SporOkullari/SporOkullariCekilis"));
const SporOkullariSonuc = lazy(() => import("./pages/SporOkullari/SporOkullariSonuc"));
const QrKod = lazy(() => import("./pages/QrKod/QrKod"));
const Kurek = lazy(() => import("./pages/Kano/Kano"));
const Results = lazy(() => import("./pages/Kano/Results"));
const PazarYerleri = lazy(() => import("./pages/PazarYerleri/PazarYerleri"));
const Parklar = lazy(() => import("./pages/Parklar/Parklar"));
const AtikYonetim = lazy(() => import("./pages/AtikYonetim/AtikYonetim"));
const Tesislerimiz = lazy(() => import("./pages/Tesislerimiz/Tesislerimiz"));
const SPGizlilikSozlesmesi = lazy(() => import("./pages/SerdivanCepteApp/SPGizlilikSozlesmesi"));
const KullanİmKosullariGizlilikPolitikasi = lazy(() => import("./pages/SerdivanCepteApp/KullanİmKosullariGizlilikPolitikasi"));
const SABSIS = lazy(() => import("./pages/SABSIS/SABSIS"));
const SerdivanCepteMarket = lazy(() => import("./pages/SerdivanCepteApp/SerdivanCepteMarket"));

// Özelleştirilmiş Yükleme Bileşeni
const PageLoader = () => (
  <div
    style={{
      position: "fixed", inset: 0, zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: "1.6rem", background: "#fff", fontFamily: "Ubuntu, system-ui, sans-serif",
    }}
  >
    <img
      src="/images/serdivanlogo.webp"
      width="76"
      height="78"
      alt="Serdivan Belediyesi"
      style={{ animation: "app-logo 1.6s ease-in-out infinite" }}
    />
    <div style={{ width: 190, height: 4, background: "#e8ede9", borderRadius: 99, overflow: "hidden", position: "relative" }}>
      <div style={{ position: "absolute", top: 0, left: 0, width: "42%", height: "100%", background: "linear-gradient(90deg,#3d8361,#6cc297)", borderRadius: 99, animation: "app-bar 1.15s ease-in-out infinite" }} />
    </div>
    <style>{`@keyframes app-logo{0%,100%{opacity:.5;transform:scale(.95)}50%{opacity:1;transform:scale(1)}}@keyframes app-bar{0%{left:-42%}100%{left:100%}}`}</style>
  </div>
);

function App() {
  const location = useLocation();
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [bgIndex, setBgIndex] = useState(0);
  const [isBgLoaded, setIsBgLoaded] = useState(false);
  const [ setHaberlerBg] = useState(null);
  const [pendingBg, setPendingBg] = useState(null);
  const [bgTransitioning, setBgTransitioning] = useState(false);
  const [ setBgFade] = useState(1);
  const timeoutRef = useRef();

  const handleSlideNext = () => {
    if (swiperInstance) swiperInstance.slideNext();
  };

  const handleSlidePrev = () => {
    if (swiperInstance) swiperInstance.slidePrev();
  };

  const goToFirstSlide = () => {
    if (location.pathname === "/" && swiperInstance) {
      swiperInstance.slideTo(0);
    }
  };

  useEffect(() => {
    // Elfsight platform.js (Google Maps + platform ~224KB çekiyor) KRİTİK YOLDAN ÇIKARILDI.
    // Sayfada elfsight-app widget'ı yoksa hiç yükleme; varsa da boşta (idle) yükle ki
    // anasayfa LCP/TBT'yi bloklamasın. (Kodda widget yok; CMS'e gömülü olursa yine çalışır.)
    if (document.querySelector('script[src="https://static.elfsight.com/platform/platform.js"]')) return;
    const load = () => {
      if (document.querySelector('script[src="https://static.elfsight.com/platform/platform.js"]')) return;
      const script = document.createElement('script');
      script.src = 'https://static.elfsight.com/platform/platform.js';
      script.async = true;
      document.body.appendChild(script);
    };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(load, { timeout: 5000 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    // Route değişince Swiper index'i sıfırla ve arka plan class'ını kaldır
    setActiveIndex(0);
    document.body.classList.remove("footer-slide-active");
    document.documentElement.classList.remove("footer-slide-active");
    document.getElementById("root")?.classList.remove("footer-slide-active");
  }, [location.pathname]);

  useEffect(() => {
    // Swiper slide değişiminde arka plan class'ını yönet
    if (activeIndex === 3) {
      document.body.classList.add("footer-slide-active");
      document.documentElement.classList.add("footer-slide-active");
      document.getElementById("root")?.classList.add("footer-slide-active");
    } else {
      document.body.classList.remove("footer-slide-active");
      document.documentElement.classList.remove("footer-slide-active");
      document.getElementById("root")?.classList.remove("footer-slide-active");
    }
  }, [activeIndex]);

  useEffect(() => {
    if (location.pathname === "/" && location.state?.goToSlide !== undefined && swiperInstance) {
      swiperInstance.slideTo(location.state.goToSlide);
    }
  }, [location, swiperInstance]);

  // Örnekteki indicator animasyonu ile senkronize arka plan geçişi (6 saniye)
  useEffect(() => {
    const isHomeFirstSlide = location.pathname === "/" && activeIndex === 0;
    if (!isHomeFirstSlide) {
      setBgIndex(0);
      setIsBgLoaded(false);
      return () => {};
    }
    if (isBgLoaded) {
      // Örnekteki loop süresine uygun olarak 6 saniye
      timeoutRef.current = setTimeout(() => {
        setIsBgLoaded(false);
        setBgIndex((prev) => (prev + 1) % 17); // SliderImages ile dinamik olacak
      }, 4000); // Haberler.jsx'deki loop ile aynı süre
    }
    return () => clearTimeout(timeoutRef.current);
  }, [location.pathname, activeIndex, bgIndex, isBgLoaded]);

  const noNavbarFooterPaths = ['/trafikpark', '/planetaryum', '/kurek', '/sabsis-serdivan-afet-bilgi-sistemi', '/pazar-yerleri', '/atik-yonetim-rehberi'];
  const noNavbarPaths = ['/trafikpark', '/planetaryum', '/kurek', '/sabsis-serdivan-afet-bilgi-sistemi', '/pazar-yerleri', '/atik-yonetim-rehberi', '/parklar'];
  const isHomePage = location.pathname === '/';

  const shouldShowNavbar = !noNavbarPaths.some(path => location.pathname.startsWith(path));
  const shouldShowFooter = !noNavbarFooterPaths.some(path => location.pathname.startsWith(path)) && !isHomePage;


  // Fade geçişi yönetimi
  useEffect(() => {
    if (!bgTransitioning || !pendingBg) return;
    // 1. Eskiyi fade out
    setBgFade(0);
    const timeout = setTimeout(() => {
      setHaberlerBg(pendingBg);
      setBgFade(1);
      setPendingBg(null);
      setBgTransitioning(false);
    }, 400); // fade süresi
    return () => clearTimeout(timeout);
  }, [bgTransitioning, pendingBg]);
  useEffect(() => {
    function setAppVh() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--app-vh', `${vh}px`);
    }
    
    setAppVh();
    
    // Scroll davranışını iyileştir
    document.documentElement.style.scrollBehavior = 'smooth';
    document.body.style.overflowX = 'hidden';
    
    const handleResize = () => {
      setAppVh();
      // Resize sonrası scroll pozisyonunu KORU - sıfırlama!
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Google Analytics'i bir kez başlat — kritik yolu bloklamasın diye boşta (idle) yükle.
  // (GTM/gtag ~157KB + ~270ms CPU; TBT'yi düşürmek için ilk render'dan sonra başlatılıyor.)
  useEffect(() => {
    let cancelled = false;
    const start = () => { if (!cancelled) initializeAnalytics(); };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(start, { timeout: 4000 });
      return () => { cancelled = true; window.cancelIdleCallback && window.cancelIdleCallback(id); };
    }
    const t = setTimeout(start, 2500);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  // index.html'deki anlık yükleme spinner'ını React ilk boyadıktan sonra kaldır.
  // (React PageLoader'ı aynı göründüğü için devralma kesintisiz olur.)
  useEffect(() => {
    const el = document.getElementById("app-loading");
    if (el) el.remove();
  }, []);

  // Route değiştikçe sayfa görüntüleme takibi yap
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Canonical default: <Seo> KULLANMAYAN sayfalarda canonical'ı mevcut path'e ayarla.
  // index.html statik canonical'ı anasayfa ("/") olduğundan, <Seo> içermeyen her içerik
  // sayfası "canonical anasayfaya işaret ediyor" SEO hatası alıyordu. <Seo> kullanan
  // sayfalar data-seo-managed işaretiyle bunu devre dışı bırakıp kendi (alias dedupe gibi
  // daha spesifik) canonical'ını korur. Sadece <head> linkine dokunur; layout etkisi yok.
  useEffect(() => {
    if (document.documentElement.hasAttribute("data-seo-managed")) return;
    const href = "https://serdivan.bel.tr" + location.pathname;
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);
    const og = document.head.querySelector('meta[property="og:url"]');
    if (og) og.setAttribute("content", href);
  }, [location.pathname]);

  // Swiper ölçülerini mount/resize/load'da yenile -> refresh'te bozuk açılmayı önler
  useEffect(() => {
    if (!swiperInstance) return;
    const refreshSwiper = () => {
      requestAnimationFrame(() => {
        try { swiperInstance.update(); } catch (e) { /* noop */ }
      });
    };
    refreshSwiper();
    window.addEventListener('resize', refreshSwiper);
    window.addEventListener('orientationchange', refreshSwiper);
    window.addEventListener('load', refreshSwiper);
    return () => {
      window.removeEventListener('resize', refreshSwiper);
      window.removeEventListener('orientationchange', refreshSwiper);
      window.removeEventListener('load', refreshSwiper);
    };
  }, [swiperInstance]);

  return (
    <div
      className={`app${isHomePage ? ' app--home' : ' app--other'}${activeIndex === 4 ? ' footer-slide-active' : ''}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        ...(isHomePage
          ? {
              height: "calc(var(--app-vh, 1vh) * 100)",
              width: "100vw"
            }
          : {
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              overflowX: 'hidden'
            }),
        ...(isHomePage && activeIndex === 3 ? { background: '#23272f', backgroundImage: 'none' } : {})
      }}
    >
      <WeeklyPopup />
      {/* Slider arka planı: sade fade-in efektli */} 
      {isHomePage && activeIndex === 0 && (
        <SliderImages key="slider-hero-bg">
          {(sliderImages) => (
            <>
              <img
                src={sliderImages[bgIndex]?.displayUrl || sliderImages[bgIndex]?.url}
                alt=""
                fetchpriority="high"
                decoding="async"
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  objectFit: "cover",
                  zIndex: 0,
                  opacity: isBgLoaded ? 1 : 0,
                  transition: "opacity 0.35s cubic-bezier(0.445, 0.05, 0.55, 0.95)",
                  pointerEvents: "none",
                  userSelect: "none"
                }}
                draggable={false}
                onLoad={() => setIsBgLoaded(true)}
              />
              <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.45)",
                zIndex: 1,
                pointerEvents: "none"
              }} />
            </>
          )}
        </SliderImages>
      )}

      {/* ScrollToTop'u sadece home olmayan sayfalarda render et */}
      {!isHomePage && <ScrollToTop />}
      
      <main style={{
        flex: 1,
        overflow: isHomePage ? 'hidden' : 'visible',
        position: 'relative',
        // CLS: home-dışı sayfalarda içerik async (CMS) yüklenirken kısa "Yükleniyor"
        // durumu footer'ı görünür kılıyor, veri gelince footer aşağı kayıp layout
        // shift (CLS) üretiyordu. min-height:100vh ile footer yükleme sırasında
        // fold'un altında kalır -> içerik gelince kaymaz. (Standart sticky-footer.)
        minHeight: isHomePage ? undefined : '100vh'
      }}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={
              <div
                className={`app--home${activeIndex === 4 ? ' footer-slide-active' : ''}`}
                style={{
                  position: "relative",
                  width: "100vw",
                  height: "calc(var(--app-vh, 1vh) * 100)",
                  overflow: "hidden",
                  background: isHomePage && activeIndex === 3 ? '#23272f' : undefined,
                  touchAction: 'pan-y'
                }}
              >
                {/* Main Header - positioned above Swiper - Hide on FooterSlide */}
                {activeIndex !== 3 && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "90px",
                      zIndex: 100, // Çok daha düşük
                      pointerEvents: "auto"
                    }}
                  >
                    <Header goToFirstSlide={goToFirstSlide} />
                  </div>
                )}

                <Swiper
                  modules={[Mousewheel, FreeMode, Keyboard]}
                  keyboard={{
                    enabled: true,
                  }}
                  direction="vertical"
                  slidesPerView={1}
                  freeMode={{
                    enabled: false,
                    sticky: true,
                    momentumRatio: 0.25,
                    momentumVelocityRatio: 0.25
                  }}
                  mousewheel={{
                    enabled: true,
                    forceToAxis: true,
                    sensitivity: 1,
                    thresholdDelta: 50,
                    thresholdTime: 500
                  }}
                  touchRatio={1}
                  touchAngle={45}
                  grabCursor={false}
                  allowTouchMove={true}
                  resistanceRatio={0.85}
                  speed={800}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "calc(var(--app-vh, 1vh) * 100)",
                    overflow: "hidden"
                  }}
                  className="home-swiper"
                  onSwiper={setSwiperInstance}
                  onSlideChange={(swiper) => {
                    setActiveIndex(swiper.activeIndex);
                    // Gereksiz scroll sıfırlamayı kaldır
                  }}
                >
                  <SwiperSlide style={{ 
                    position: "relative", 
                    width: "100vw", 
                    height: "calc(var(--app-vh, 1vh) * 100)",
                    overflow: "hidden"
                  }}>
                    <HeroSlide goToFirstSlide={goToFirstSlide} activeIndex={activeIndex} />
                  </SwiperSlide>
                  <SwiperSlide style={{ 
                    position: "relative", 
                    width: "100vw", 
                    height: "calc(var(--app-vh, 1vh) * 100)",
                    overflow: "hidden"
                  }}>
                    <NewsSlide goToFirstSlide={goToFirstSlide} />
                  </SwiperSlide>
                  <SwiperSlide style={{ 
                    position: "relative", 
                    width: "100vw", 
                    height: "calc(var(--app-vh, 1vh) * 100)",
                    overflow: "hidden"
                  }}>
                    <CalendarSlide goToFirstSlide={goToFirstSlide} />
                  </SwiperSlide>
                  <SwiperSlide style={{ 
                    position: "relative", 
                    width: "100vw", 
                    height: "calc(var(--app-vh, 1vh) * 100)",
                    overflow: "hidden"
                  }}>
                    <FooterSlide active={activeIndex === 3} />
                  </SwiperSlide>
                </Swiper>
                
                {/* Navigation butonları */}
                <div className="custom-swiper-navigation" style={{ zIndex: 90 }}> {/* Çok daha düşük */}
                  {activeIndex !== 0 && (
                    <button onClick={handleSlidePrev} className="swiper-button-prev-custom" aria-label="Yukarı Kaydır">&uarr;</button>
                  )}
                  <div className="slider-nav-arrow-spacer" />
                  {activeIndex !== 3 && (
                    <button onClick={handleSlideNext} className="swiper-button-next-custom" aria-label="Aşağı Kaydır">&darr;</button>
                  )}
                </div>
              </div>
            } />
            
            <Route path="/serdivan-tarihi" element={<SerdivanTarihi />} />
            <Route path="/serdivan-cografya" element={<SerdivanCografya />} />
            <Route path="/serdivan-gelisim" element={<SerdivanGelisim />} />
            <Route path="/muhtarliklar" element={<Muhtarliklar />} />
            <Route path="/kardes-sehirler" element={<KardesSehirler />} />
            <Route path="/belediye-baskani" element={<BelediyeBaskani />} />
            <Route path="/iletisim" element={<Iletisim />} />
            <Route path="/meclisgorevleri" element={<MeclisGorevleri />} />
            <Route path="/hizmetlerimiz" element={<Hizmetlerimiz />} />
            <Route path="/hizmetler" element={<Hizmetlerimiz />} />
            <Route path="/hizmetler/:slug" element={<Hizmetlerimiz />} />
            <Route path="/duyurular" element={<Duyurular />} />
            <Route path="/etik" element={<Etik />} />
            <Route path="/vizyon-ve-misyon" element={<Vizyon />} />
            <Route path="/stratejik-planlar" element={<StratejikPlanlar />} />
            <Route path="/performans-programlari" element={<PerformansProgramlari />} />
            <Route path="/faaliyet-raporlari" element={<FaaliyetRaporlari />} />
            <Route path="/mali-beklenti" element={<MaliBeklenti />} />
            <Route path="/encumen-gorevleri" element={<EncumenGorev />} />
            <Route path="/haberler" element={<Haberler />} />
            <Route path="/haberler/:slug" element={<HaberGoruntuleme />} />
            <Route path="/organizasyon-semasi" element={<OrganizasyonSemasiPage />} />
            <Route path="/meclis-uyeleri" element={<MeclisUyeleri />} />
            <Route path="/meclis-gundemleri" element={<MeclisGundemleri />} />
            <Route path="/meclis-kararlari" element={<MeclisKararlari />} />
            <Route path="/komisyon-raporlari" element={<KomisyonRaporlari />} />
            <Route path="/baskan-yardimcilari" element={<BaskanYardimcilari />} />
            <Route path="/belediye-encumeni" element={<BelediyeEncumeni />} />
            <Route path="/onlineislemler" element={<OnlineIslemler />} />
            <Route path="/mudurlukler" element={<Mudurlukler />} />
            <Route path="/kvkk" element={<Kvkk />} />
            <Route path="/trafikpark" element={<Trafikpark />} />
            <Route path="/planetaryum" element={<Planetaryum />} />
            <Route path="/kurek" element={<Kurek />} />  
            <Route path="/kurek/sonuclar" element={<Results />} />
            <Route path="/parklar" element={<Parklar />} />
            <Route path="/pazar-yerleri" element={<PazarYerleri />} />
            <Route path="/atik-yonetim-rehberi" element={<AtikYonetim />} />
            <Route path="/tesislerimiz" element={<Tesislerimiz />} />
            <Route path="/planetaryum/galeri" element={<PlanetaryumGaleri />} />
            <Route path="/trafikpark/galeri" element={<TrafikparkGaleri />} />
            <Route path="/serbim" element={<Serbim />} />
            <Route path="/nobetci-eczaneler" element={<NobetciEczaneler />} />
            <Route path="/etkinlik-takvimi" element={<EtkinlikTakvimi />} />
            <Route path="/etkinlikler" element={<EtkinlikTakvimi />} />
            <Route path="/etkinlik/:slug" element={<EventDetail />} />
            <Route path="/serdivan-spor-okullari-kayitlari" element={<SporOkullariKayit />} />
            <Route path="/serdivan-spor-okullari-cekilis" element={<SporOkullariCekilis />} />
            <Route path="/serdivan-spor-okullari-sonuclari" element={<SporOkullariSonuc />} />
            <Route path="/qr" element={<QrKod />} />
            <Route path="/SerdivanCepte-uygulamasi-gizlilik-sozlesmesi" element={<SPGizlilikSozlesmesi />} />
            <Route path="/SerdivanCepte-uygulamasi-kullanim-kosullari-ve-gizlilik-politikasi" element={<KullanİmKosullariGizlilikPolitikasi />} />
            <Route path="/sabsis-serdivan-afet-bilgi-sistemi" element={<SABSIS />} />
            <Route path="/app" element={<SerdivanCepteMarket />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      {shouldShowNavbar && !isHomePage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "90px",
            zIndex: 100, // Çok daha düşük
          }}
        >
          <Header goToFirstSlide={goToFirstSlide} />
        </div>
      )}
      {shouldShowFooter && <div><Footer /></div>}
    </div>
  );
}

export default App;
